import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import backofficeApiClient, {
  CompanyRequestModel,
  SelfEmployedRequestModel,
  InternationalRequestModel,
  EntityInformationRequestModel,
  BankingInformationRequestModel,
  ProfileCorporateBillingInfoModel,
  BOUsersPriceAgreementConfirmModel,
  ProfileCorporateSupervisorsInfoModel,
  ProfileCorporatePrimaryContactsInfoModel,
  ProfileCorporateTimesheetApproversInfoModel,
  FullBOUserResponseModel,
  ProfileCorporatePrimaryContactInfoModel,
  ProfileCorporateSupervisorInfoModel,
  ProfileCorporateTimesheetApproverInfoModel,
  MemberAdditionalInfoReplyRequestModel,
  BOUsersHideIndicatorModel,
  UserEmailSettingsModel,
  UpdatePasswordRequestModel,
  TwoFactorAuthSendMessageTypeEnum,
} from 'api/backofficeApiClient';
import { logout } from './authenticationSlice';
import adminApiClient from 'api/adminApiClient';
import accountApiClient, { ApplicationTypeEnum } from 'api/accountApiClient';

interface AccountState {
  myProfile?: FullBOUserResponseModel;
  primaryContacts: ProfileCorporatePrimaryContactInfoModel[];
  isPrimaryContactsLoading: boolean;
  supervisors: ProfileCorporateSupervisorInfoModel[];
  isSupervisorsLoading: boolean;
  timesheetApprovers: ProfileCorporateTimesheetApproverInfoModel[];
  isTimesheetApproversLoading: boolean;
}

const initialState: AccountState = {
  myProfile: undefined,
  primaryContacts: [],
  isPrimaryContactsLoading: false,
  supervisors: [],
  isSupervisorsLoading: false,
  timesheetApprovers: [],
  isTimesheetApproversLoading: false,
};

const getFullUserProfile = createAsyncThunk(
  'account/getFullUserProfile ',
  async () => {
    return backofficeApiClient.getMyFullProfile();
  },
);

const saveCompanyGeneralInfo = createAsyncThunk(
  'account/saveCompanyGeneralInfo',
  async (profile: CompanyRequestModel) => {
    return backofficeApiClient.updateCompanyProfile(profile);
  },
);

const saveSelfEmployedGeneralInfo = createAsyncThunk(
  'account/saveSelfEmployedGeneralInfo',
  async (profile: SelfEmployedRequestModel) => {
    return backofficeApiClient.updateSelfEmployeeProfile(profile);
  },
);

const saveInternationalGeneralInfo = createAsyncThunk(
  'account/saveInternationalGeneralInfo',
  async (profile: InternationalRequestModel) => {
    return backofficeApiClient.updateInternationalProfile(profile);
  },
);

const getBankingInformation = createAsyncThunk(
  'account/getBankingInformation',
  async () => {
    return backofficeApiClient.getBankingInformation();
  },
);

interface SaveBankingInfoOptions {
  bankingInfo: BankingInformationRequestModel;
}
const saveBankingInformation = createAsyncThunk(
  'account/saveBankingInformation',
  async ({ bankingInfo }: SaveBankingInfoOptions) => {
    return backofficeApiClient.createOrUpdateBankingInformation(bankingInfo);
  },
);

const getEntityInformation = createAsyncThunk(
  'account/getEntityInformation',
  async () => {
    return backofficeApiClient.getEntityInformation();
  },
);

const getEntityInfoFile = createAsyncThunk(
  'account/getEntityInfoFile',
  async () => {
    return backofficeApiClient.getEntityInfoFile();
  },
);

const getBankingInfoFile = createAsyncThunk(
  'account/getBankingInfoFile',
  async () => {
    return backofficeApiClient.getBankingInfoFile();
  },
);

const getPriceAgreementInfoFile = createAsyncThunk(
  'account/getPriceAgreementInfoFile',
  async () => {
    return backofficeApiClient.getPriceAgreementFile();
  },
);

const getEmailDeliveryConfiguration = createAsyncThunk(
  'account/getEmailDeliveryConfiguration',
  async () => {
    return backofficeApiClient.getUserEmailSettingsForCurrentMember();
  },
);

const updateEmailDeliveryConfiguration = createAsyncThunk(
  'account/updateEmailDeliveryConfiguration',
  async (data: UserEmailSettingsModel) => {
    return backofficeApiClient.updateUserEmailSettings(data);
  },
);

const updateEntityInformation = createAsyncThunk(
  'account/updateEntityInformation',
  async (entityInfo: EntityInformationRequestModel) => {
    return backofficeApiClient.createOrUpdateEntityInformation(entityInfo);
  },
);

const getBillingInformation = createAsyncThunk(
  'account/getBillingInformation',
  async () => {
    return backofficeApiClient.getCorporateBillingInfo();
  },
);

interface UpdateBillingInfoOptions {
  billingInfo: ProfileCorporateBillingInfoModel;
}
const updateBillingInformation = createAsyncThunk(
  'account/updateBillingInformation',
  async ({ billingInfo }: UpdateBillingInfoOptions) => {
    return backofficeApiClient.setCorporateBillingInfo(billingInfo);
  },
);

const getSupervisorInformation = createAsyncThunk(
  'account/getSupervisorInformation',
  async () => {
    return backofficeApiClient.getCorporateSupervisors();
  },
);

interface UpdateSupervisorInfoOptions {
  supervisorsData: ProfileCorporateSupervisorsInfoModel;
}
const updateSupervisorInformation = createAsyncThunk(
  'account/updateSupervisorInformation',
  async ({ supervisorsData }: UpdateSupervisorInfoOptions) => {
    return backofficeApiClient.setCorporateSupervisorsInfo(supervisorsData);
  },
);

const getTimesheetApproverInformation = createAsyncThunk(
  'account/getTimesheetApproverInformation',
  async () => {
    return backofficeApiClient.getCorporateTimesheetApprovers();
  },
);

interface UpdateTimesheetApproverInfoOptions {
  timesheetApproversData: ProfileCorporateTimesheetApproversInfoModel;
}
const updateTimesheetApproverInformation = createAsyncThunk(
  'account/updateTimesheetApproverInformation',
  async ({ timesheetApproversData }: UpdateTimesheetApproverInfoOptions) => {
    return backofficeApiClient.setCorporateTimesheetApproversInfo(
      timesheetApproversData,
    );
  },
);

const getPrimaryContactInfo = createAsyncThunk(
  'account/getPrimaryContactInfo',
  async () => {
    return backofficeApiClient.getCorporatePrimaryContacts();
  },
);

interface UpdatePrimaryContactInfoOptions {
  primaryContactsData: ProfileCorporatePrimaryContactsInfoModel;
}
const updatePrimaryContactInfo = createAsyncThunk(
  'account/updatePrimaryContactInfo',
  async ({ primaryContactsData }: UpdatePrimaryContactInfoOptions) => {
    return backofficeApiClient.setCorporatePrimaryContactsInfo(
      primaryContactsData,
    );
  },
);

const getPricingAgreement = createAsyncThunk(
  'account/getPricingAgreement',
  async () => {
    return backofficeApiClient.getPriceAgreement();
  },
);

const confirmPricingAgreement = createAsyncThunk(
  'account/confirmPricingAgreement',
  async (data: BOUsersPriceAgreementConfirmModel) => {
    return backofficeApiClient.confirmPriceAgreement(data);
  },
);

interface GetCorporateTAContactData {
  isAdmin?: boolean;
  companyId?: string;
}

const getCorporateTAPrimaryContacts = createAsyncThunk(
  'account/getCorporateTAPrimaryContacts',
  async ({ isAdmin, companyId }: GetCorporateTAContactData) => {
    return isAdmin
      ? adminApiClient.getCorporatePrimaryContactsForMember(companyId as string)
      : backofficeApiClient.getCorporatePrimaryContacts();
  },
);

const getCorporateTASupervisors = createAsyncThunk(
  'account/getCorporateTASupervisors',
  async ({ isAdmin, companyId }: GetCorporateTAContactData) => {
    return isAdmin
      ? adminApiClient.getCorporateSupervisorsForMember(companyId as string)
      : backofficeApiClient.getCorporateSupervisors();
  },
);

const getCorporateTATimesheetApprovers = createAsyncThunk(
  'account/getCorporateTATimesheetApprovers',
  async ({ isAdmin, companyId }: GetCorporateTAContactData) => {
    return isAdmin
      ? adminApiClient.getCorporateTimesheetApproversForMember(
          companyId as string,
        )
      : backofficeApiClient.getCorporateTimesheetApprovers();
  },
);

const checkIfProfileIsCompleted = createAsyncThunk(
  'account/checkIfProfileIsCompleted',
  async () => {
    return backofficeApiClient.hasProfileCompleted();
  },
);

const checkIfOnlyBillingInfoUnfilled = createAsyncThunk(
  'account/checkIfBillingInfoFilled',
  async () => {
    return backofficeApiClient.hasOnlyBillingInformationUnfilled();
  },
);

interface CanRemoveContactOptions {
  isAdmin?: boolean;
  contactId: string;
}
const checkIsContactUsedInActiveAssignment = createAsyncThunk(
  'account/checkIsContactUsedInActiveAssignment',
  async ({ isAdmin, contactId }: CanRemoveContactOptions) => {
    return isAdmin
      ? adminApiClient.contactHasActiveAssignmentRelation(contactId)
      : backofficeApiClient.contactHasActiveAssignmentRelation(contactId);
  },
);

const sendMemberAdditionalInfoResponse = createAsyncThunk(
  'account/sendMemberAdditionalInfoResponse',
  async (responseInfo: MemberAdditionalInfoReplyRequestModel) => {
    await backofficeApiClient.sendMemberAdditionalInfoReply(responseInfo);

    return backofficeApiClient.getMyFullProfile();
  },
);

const hideMemberPulsatingIndicator = createAsyncThunk(
  'account/hideMemberPulsatingIndicator',
  async (stageInfo: BOUsersHideIndicatorModel) => {
    await backofficeApiClient.hideUserPulsatingIndicator(stageInfo);

    return backofficeApiClient.getMyFullProfile();
  },
);

const changePassword = createAsyncThunk(
  'account/changePassword',
  async (data: UpdatePasswordRequestModel) => {
    return await backofficeApiClient.updatePassword(data);
  },
);

const changePasswordIsActiveTwoFA = createAsyncThunk(
  'account/changePasswordActiveTwoFA',
  async (data: UpdatePasswordRequestModel, { rejectWithValue }) => {
    try {
      const response = await backofficeApiClient.updatePassword(data);
      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const resendTwoFactorAuthCode = createAsyncThunk(
  'auth/resendTwoFactorAuthCode',
  async (authCode: string) => {
    return accountApiClient.resendTwoFactorAuthCode(
      authCode,
      ApplicationTypeEnum.Backoffice,
    );
  },
);

const sendCodeActivateTwoFA = createAsyncThunk(
  'account/sendCodeActivateTwoFA',
  async (
    messageTypeEnum: TwoFactorAuthSendMessageTypeEnum,
    { rejectWithValue },
  ) => {
    try {
      const response = await backofficeApiClient.sendTwoFactorAuthCode(
        messageTypeEnum,
      );
      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      Object.assign(state, initialState);
    });
    builder.addCase(getFullUserProfile.pending, (state) => {
      state.myProfile = undefined;
    });
    builder.addCase(getFullUserProfile.fulfilled, (state, action) => {
      state.myProfile = action.payload;
    });
    builder.addCase(getFullUserProfile.rejected, (state) => {
      state.myProfile = undefined;
    });
    builder.addCase(
      sendMemberAdditionalInfoResponse.fulfilled,
      (state, action) => {
        state.myProfile = action.payload;
      },
    );
    builder.addCase(hideMemberPulsatingIndicator.fulfilled, (state, action) => {
      state.myProfile = action.payload;
    });
    builder.addCase(getCorporateTAPrimaryContacts.pending, (state) => {
      state.primaryContacts = [];
      state.isPrimaryContactsLoading = true;
    });
    builder.addCase(
      getCorporateTAPrimaryContacts.fulfilled,
      (state, action) => {
        state.primaryContacts = action.payload;
        state.isPrimaryContactsLoading = false;
      },
    );
    builder.addCase(getCorporateTAPrimaryContacts.rejected, (state) => {
      state.primaryContacts = [];
      state.isPrimaryContactsLoading = false;
    });
    builder.addCase(getCorporateTASupervisors.pending, (state) => {
      state.supervisors = [];
      state.isSupervisorsLoading = true;
    });
    builder.addCase(getCorporateTASupervisors.fulfilled, (state, action) => {
      state.supervisors = action.payload;
      state.isSupervisorsLoading = false;
    });
    builder.addCase(getCorporateTASupervisors.rejected, (state) => {
      state.supervisors = [];
      state.isSupervisorsLoading = false;
    });
    builder.addCase(getCorporateTATimesheetApprovers.pending, (state) => {
      state.timesheetApprovers = [];
      state.isTimesheetApproversLoading = true;
    });
    builder.addCase(
      getCorporateTATimesheetApprovers.fulfilled,
      (state, action) => {
        state.timesheetApprovers = action.payload;
        state.isTimesheetApproversLoading = false;
      },
    );
    builder.addCase(getCorporateTATimesheetApprovers.rejected, (state) => {
      state.timesheetApprovers = [];
      state.isTimesheetApproversLoading = false;
    });
  },
});

const { reducer } = accountSlice;

export {
  getEntityInfoFile,
  getEntityInformation,
  updateEntityInformation,
  getFullUserProfile,
  saveCompanyGeneralInfo,
  saveSelfEmployedGeneralInfo,
  saveInternationalGeneralInfo,
  getBankingInformation,
  saveBankingInformation,
  getPricingAgreement,
  confirmPricingAgreement,
  getCorporateTAPrimaryContacts,
  getCorporateTASupervisors,
  getPriceAgreementInfoFile,
  getCorporateTATimesheetApprovers,
  getBillingInformation,
  updateBillingInformation,
  getSupervisorInformation,
  updateSupervisorInformation,
  getPrimaryContactInfo,
  updatePrimaryContactInfo,
  getTimesheetApproverInformation,
  updateTimesheetApproverInformation,
  sendMemberAdditionalInfoResponse,
  checkIsContactUsedInActiveAssignment,
  checkIfProfileIsCompleted,
  checkIfOnlyBillingInfoUnfilled,
  hideMemberPulsatingIndicator,
  getBankingInfoFile,
  getEmailDeliveryConfiguration,
  updateEmailDeliveryConfiguration,
  changePassword,
  resendTwoFactorAuthCode,
  sendCodeActivateTwoFA,
  changePasswordIsActiveTwoFA,
};
export default reducer;
